import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const { branding } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect standalone PWA mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    setIsStandalone(standalone);

    // Detect OS & device
    const ua = window.navigator.userAgent.toLowerCase();
    const iosDevice = /iphone|ipad|ipod/.test(ua) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const mobileDevice = iosDevice || /android|webos|blackberry|iemobile|opera mini/i.test(ua) || window.innerWidth < 768;
    setIsIos(iosDevice);
    setIsMobile(mobileDevice);

    const listener = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', listener);

    // Check if dismissed recently (in the last 24 hours)
    const dismissedAt = localStorage.getItem('pickle_install_dismissed');
    const recentlyDismissed = dismissedAt && (Date.now() - Number(dismissedAt)) < 24 * 60 * 60 * 1000;

    // Trigger popup on mobile if not already installed and not dismissed recently
    if (mobileDevice && !standalone && !recentlyDismissed) {
      const timer = window.setTimeout(() => {
        setShowModal(true);
      }, 1200);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', listener);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', listener);
  }, []);

  const handleDismiss = () => {
    setShowModal(false);
    localStorage.setItem('pickle_install_dismissed', String(Date.now()));
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setShowModal(false);
        setDeferredPrompt(null);
        localStorage.setItem('pickle_install_dismissed', String(Date.now()));
      }
    }
  };

  if (isStandalone) return null;

  const appName = branding?.appName || 'Pickle Social';

  return (
    <>


      {/* Desktop Sidebar Install Button */}
      {deferredPrompt && document.getElementById('desktop-sidebar') && !isMobile &&
        createPortal(
          <div className="mt-2 px-4 pb-4">
            <button
              type="button"
              onClick={handleInstallClick}
              className="w-full rounded-xl bg-white/20 px-3 py-2 text-xs font-bold text-white hover:bg-white/30 transition flex items-center justify-center gap-2"
            >
              <span>📲</span>
              <span>Install Web App</span>
            </button>
          </div>,
          document.getElementById('desktop-sidebar')!
        )}

      {/* Mobile Install Popup Dialog */}
      {showModal &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-950/60 p-0 sm:p-4 backdrop-blur-sm transition-opacity"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleDismiss();
            }}
          >
            <div className="w-full max-w-md max-h-[85dvh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-white p-5 sm:p-6 pb-[max(1.5rem,calc(env(safe-area-inset-bottom)+1rem))] shadow-2xl border border-slate-100 overflow-y-auto">
              <div className="flex items-start justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3">
                  {branding?.homeIcon || branding?.appLogo ? (
                    <img
                      src={branding.homeIcon || branding.appLogo || ''}
                      alt=""
                      className="h-12 w-12 rounded-2xl object-cover shadow-md border border-slate-200"
                    />
                  ) : (
                    <div className="grid h-12 w-12 place-items-center rounded-2xl brand-bg text-2xl text-white shadow-md">
                      🎾
                    </div>
                  )}
                  <div>
                    <span className="chip bg-emerald-100 text-emerald-800 text-[10px] font-extrabold uppercase">
                      Home Screen App
                    </span>
                    <h3 className="text-lg font-extrabold brand-text mt-0.5">
                      Install {appName}
                    </h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-500 font-bold hover:bg-slate-200"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <p className="mt-3 text-xs leading-5 text-slate-600 shrink-0">
                Install this app on your home screen for quick 1-tap access on court, fullscreen view, and seamless scorekeeping.
              </p>

              {/* Platform Instructions */}
              {isIos ? (
                <div className="mt-4 rounded-2xl bg-slate-50 p-3.5 sm:p-4 border border-slate-200/80 space-y-2.5">
                  <p className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                    <span>🍎</span>
                    <span>How to install on iPhone Safari:</span>
                  </p>
                  <div className="flex items-center gap-3 text-xs text-slate-700 bg-white p-2.5 rounded-xl border border-slate-200 shadow-xs">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg brand-bg font-extrabold text-white text-[11px]">
                      1
                    </span>
                    <p className="leading-tight">
                      Tap the <strong className="text-slate-900 font-extrabold">Share button</strong> (
                      <span className="inline-flex items-center justify-center p-1 rounded bg-slate-100 border border-slate-300 mx-1 align-middle">
                        <svg className="w-3.5 h-3.5 text-sky-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                      </span>
                      ) in Safari toolbar
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-700 bg-white p-2.5 rounded-xl border border-slate-200 shadow-xs">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg brand-bg font-extrabold text-white text-[11px]">
                      2
                    </span>
                    <p className="leading-tight">
                      Scroll down and tap <strong className="text-slate-900 font-extrabold">"Add to Home Screen"</strong> (
                      <span className="inline-flex items-center justify-center p-1 rounded bg-slate-100 border border-slate-300 mx-1 align-middle">
                        <svg className="w-3.5 h-3.5 text-slate-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                      </span>
                      )
                    </p>
                  </div>
                </div>
              ) : deferredPrompt ? (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleInstallClick}
                    className="btn-primary w-full !rounded-2xl !py-3 !text-sm font-extrabold shadow-lg flex items-center justify-center gap-2"
                  >
                    <span>📲</span>
                    <span>Add to Home Screen</span>
                  </button>
                </div>
              ) : (
                <div className="mt-4 rounded-2xl bg-slate-50 p-3.5 border border-slate-200/80 text-xs text-slate-600">
                  Tap your browser menu (<strong className="text-slate-800">⋮</strong> or <strong className="text-slate-800">Share</strong>) and select <strong className="text-slate-800">"Add to Home Screen"</strong> or <strong className="text-slate-800">"Install app"</strong>.
                </div>
              )}

              <div className="mt-4 flex gap-2 pt-1 shrink-0">
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="w-full rounded-xl py-3 text-xs font-bold text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition border border-transparent"
                >
                  Maybe later
                </button>
                {isIos && (
                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="btn-primary w-full !rounded-xl !py-3 !text-xs font-extrabold shadow-md"
                  >
                    Got it!
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

